import React, { useState } from "react";
import StudioContentLink from "./StudioContentLink";
import StudioFontPicker from "./StudioFontPicker";
import StudioTransformPanel from "./StudioTransformPanel";
import { styleSource, inheritedStyle } from "./core/styles";
import { selectionValue } from "./studioTransform";
import {
  Button,
  Field,
  NumberField,
  ColorField,
  PanelSection,
  Icon,
} from "./StudioControls";
import {
  PAPER,
  ROLES,
  getStyle,
  resolveText,
} from "./studioModel";
export default function StudioInspector({
  menus,
  onReplaceArtwork,
  replacingArtwork,
  onEdit,
  doc,
  styleGroup,
  renameStyleGroup,
  deleteStyleGroup,
  page,
  selected,
  content,
  scope,
  setScope,
  patch,
  stylePatch,
  updatePage,
  commit,
  align,
  distribute,
  duplicate,
  remove,
  reorder,
  group,
  ungroup,
  moveToPage,
  commitNodes,
  select,
  flow,
}) {
  const [moveDistance, setMoveDistance] = useState(1);
  const nodes = (
      styleGroup || scope !== "selection" ? doc.pages.flatMap((p) => p.elements) : page.elements
    ).filter((n) => selected.includes(n.id)),
    node = nodes[0],
    style = node ? getStyle(doc, node) : {};
  const targets = node ? selected : [];
  // Typography shows a shared value or "Mixed", and — for one element — where
  // each value comes from, with a reset back to the inherited value.
  const typo = (key) => (nodes.length > 1 ? selectionValue(doc, nodes, key) : { value: style[key], mixed: false });
  const source = (key, label) => {
    if (!node || nodes.length !== 1 || styleGroup || scope !== "selection") return undefined;
    const from = styleSource(doc, node, key);
    const inherited = inheritedStyle(doc, node);
    const names = { role: ROLES[node.role], textStyle: from.name, default: "default" };
    return {
      level: from.level,
      label: from.level === "element" ? "This element" : names[from.level] || "Default",
      inherits: node.styleRef ? doc.textStyles?.[node.styleRef]?.name || "style" : ROLES[node.role],
      onReset: from.level === "element" && inherited[key] !== undefined && inherited[key] !== node.style[key]
        ? () => patch((n) => { const next = { ...n, style: { ...n.style } }; delete next.style[key]; return next; })
        : undefined,
      field: label,
    };
  };
  // Where this element sits in the menu: page › groups (sections, dishes).
  const trail = node && nodes.length === 1 && !styleGroup ? (node.groupPath || []).map((g) => ({ ...g, kind: doc.blocks?.[g.id]?.kind })) : [];
  return (
    <aside id="ms-inspector-panel" className="ms-inspector">
      <div className="ms-panel-heading">
        <h2>
          {styleGroup
            ? styleGroup.name
            : node
            ? nodes.length > 1
              ? `${nodes.length} elements`
              : ROLES[node.role]
            : "Document"}
        </h2>
        <span className="ms-kicker">{node ? "INSPECTOR" : "SETUP"}</span>
      </div>
      {styleGroup && (
        <PanelSection title="Style group">
          <Field label="Group name">
            <input
              key={`${styleGroup.role}:${styleGroup.name}`}
              defaultValue={styleGroup.name}
              maxLength={100}
              onBlur={(e) => {
                const name = e.target.value.trim();
                if (name && name !== styleGroup.name) renameStyleGroup(name);
                else e.target.value = styleGroup.name;
              }}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  e.target.value = styleGroup.name;
                  e.target.blur();
                }
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.target.blur();
                }
              }}
            />
          </Field>
          <div className="ms-scope-note">
            {nodes.length} members · {nodes.filter((n) => n.locked).length}{" "}
            locked
          </div>
          <p className="ms-hint">
            Shift-click text on any page to add or remove it. Changes apply to
            unlocked members across the menu. Names and membership are saved
            with your design.
          </p>
          <div className="ms-group-move">
            <NumberField label="Move distance · pt" value={moveDistance} min={0.1} max={1000} step={0.5} onChange={setMoveDistance} />
            <div className="ms-group-move-buttons" role="group" aria-label="Move typography group">
              {[["Left", -1, 0, "←"], ["Up", 0, -1, "↑"], ["Down", 0, 1, "↓"], ["Right", 1, 0, "→"]].map(([direction, dx, dy, arrow]) => <Button key={direction} title={`Move group ${direction.toLowerCase()}`} disabled={!nodes.some((n) => !n.locked)} onClick={() => patch((n) => ({ ...n, x: n.x + dx * moveDistance, y: n.y + dy * moveDistance }))}><span aria-hidden="true">{arrow}</span>{direction}</Button>)}
            </div>
            <p className="ms-hint">Moves unlocked members on every page. Arrow keys move 1 pt; Shift + arrow moves 10 pt.</p>
          </div>
          {styleGroup.role.startsWith("custom:") && <Button className="ms-wide" onClick={deleteStyleGroup}>Delete typography group</Button>}
          {nodes.length > 1 && (
            <p className="ms-hint">
              Fields read “Mixed” where members differ; a new value applies to all unlocked members.
            </p>
          )}
        </PanelSection>
      )}
      {!node && styleGroup ? (
        <p className="ms-hint ms-inset">
          Shift-click text to add the first member.
        </p>
      ) : !node ? (
        <>
          <PanelSection title="Menu document">
            <Field label="Document name">
              <input
                value={doc.name}
                maxLength={200}
                onChange={(e) =>
                  commit((d) => ({ ...d, name: e.target.value }))
                }
              />
            </Field>
            <p className="ms-hint">
              Your artwork is saved separately from menu content. Linked text
              follows the CMS.
            </p>
          </PanelSection>
          <PanelSection title="Page setup">
            <Field label="Page name">
              <input
                value={page.name}
                onChange={(e) => updatePage({ name: e.target.value })}
              />
            </Field>
            <Field label="Paper size">
              <select
                value={
                  Object.keys(PAPER).find(
                    (k) =>
                      PAPER[k][0] === page.width && PAPER[k][1] === page.height
                  ) || "custom"
                }
                onChange={(e) => {
                  if (PAPER[e.target.value])
                    updatePage({
                      width: PAPER[e.target.value][0],
                      height: PAPER[e.target.value][1],
                    });
                }}
              >
                <option value="custom">Custom</option>
                {Object.keys(PAPER).map((p) => (
                  <option key={p}>{p}</option>
                ))}
              </select>
            </Field>
            <div className="ms-fields-two">
              <NumberField
                label="Width · pt"
                value={page.width}
                min={72}
                max={3000}
                onChange={(width) => updatePage({ width })}
              />
              <NumberField
                label="Height · pt"
                value={page.height}
                min={72}
                max={3000}
                onChange={(height) => updatePage({ height })}
              />
            </div>
            <Button
              icon="redo"
              onClick={() =>
                updatePage({ width: page.height, height: page.width })
              }
            >
              Rotate page
            </Button>
            <NumberField
              label="Safe margin · pt"
              min={0}
              max={Math.min(page.width, page.height) / 2 - 1}
              value={page.margin}
              onChange={(margin) => updatePage({ margin })}
            />
            <ColorField
              label="Paper color"
              value={page.background}
              onChange={(background) => updatePage({ background })}
            />
            <p className="ms-hint">
              72 pt = 1 inch. Changing paper size preserves element positions.
            </p>
          </PanelSection>
          <PanelSection title="Make it yours">
            <p className="ms-hint">
              Select anything on the page to fine-tune it. Shift-click to select
              multiple elements. Double-click text to edit its print copy.
            </p>
            <div className="ms-tip">
              <Icon name="link" />
              <span>
                One style, everywhere.
                <br />
                Use <b>All matching</b> to update every section heading or price
                together.
              </span>
            </div>
          </PanelSection>
        </>
      ) : (
        <>
          {trail.length > 0 && (
            <nav className="ms-breadcrumb" aria-label="Element hierarchy">
              <span>{page.name}</span>
              {trail.map((g, i) => (
                <button key={g.id} type="button" title={g.kind ? `Select this ${g.kind}` : "Select this group"}
                  onClick={() => select?.(page.elements.filter((n) => (n.groupPath || []).some((x) => x.id === g.id)).map((n) => n.id), g.id)}>
                  › {g.name}{g.kind && g.kind !== "group" ? <small> {g.kind}</small> : null}
                </button>
              ))}
            </nav>
          )}
          {flow && (
            <PanelSection title="Section layout">
              <p className="ms-hint">{flow.pinned
                ? "Pinned: this keeps its exact position while the rest of the section flows around it."
                : "Positioned by its section. Drag to reorder, or onto another section to move it there; drag the spacing handles to change gaps (Shift for every gap)."}</p>
              <Button className="ms-wide" onClick={flow.onToggle}>{flow.pinned ? "Return to section flow" : "Pin in place"}</Button>
            </PanelSection>
          )}
          {!styleGroup && nodes.length === 1 && node.type === "text" && <StudioContentLink node={node} doc={doc} menus={menus} content={content} patch={patch} commit={commit} />}
          {!styleGroup && node.headingWinglet && (
            <PanelSection title="Heading ornament">
              <p className="ms-hint">Drag or resize this ornament freely. Its spacing stays linked to the heading.</p>
              <NumberField label="Heading gap · pt" value={node.headingWinglet.gap ?? 8} min={-100} max={300} step={1}
                onChange={(gap) => patch((n) => n.headingWinglet ? { ...n, headingWinglet: { ...n.headingWinglet, gap } } : n)} />
              <NumberField label="Vertical offset · pt" value={node.headingWinglet.offsetY || 0} min={-300} max={300} step={1}
                onChange={(offsetY) => patch((n) => n.headingWinglet ? { ...n, headingWinglet: { ...n.headingWinglet, offsetY } } : n)} />
            </PanelSection>
          )}
          {!styleGroup && (
            <PanelSection
              title="Edit scope"
              aside={<Icon name="link" size={15} />}
            >
              <Field label={node.type === "image" ? "Apply artwork changes to" : "Apply typography & color to"}>
                <select
                  value={scope}
                  onChange={(e) => setScope(e.target.value)}
                >
                  <option value="selection">Selected elements</option>
                  <option value="section" disabled={!node.sectionId}>
                    Matching in this section
                  </option>
                  <option value="page">Matching on this page</option>
                  <option value="all">All matching in document</option>
                  {node.styleRef && doc.textStyles?.[node.styleRef] && <option value="textStyle">Everything using “{doc.textStyles[node.styleRef].name}”</option>}
                </select>
              </Field>
              <div className="ms-scope-note">
                <span className="ms-dot" />
                {targets.length} {targets.length === 1 ? "element" : "elements"}{" "}
                ·{" "}
                {scope === "selection"
                  ? "Individual styling"
                  : scope === "textStyle"
                  ? "Named style"
                  : ROLES[node.role]}
              </div>
            </PanelSection>
          )}
          {nodes.every((n) => n.type === "image") && (
            <PanelSection title="Artwork">
              <Button icon="image" onClick={onReplaceArtwork} disabled={replacingArtwork || nodes.every((n) => n.locked)}>
                {replacingArtwork ? "Replacing artwork…" : "Replace artwork"}
              </Button>
              <p className="ms-hint">Keeps each frame’s position, size and orientation. PNG, JPEG or WebP · up to 2 MB.</p>
            </PanelSection>
          )}
          {node.type === "text" && (
              <PanelSection title="Typography">
                <StudioFontPicker doc={doc} value={style.fontFamily} onChange={(fontFamily) => stylePatch({ fontFamily })} />
                <div className={`ms-case-buttons${doc.menuKey === "takeout" ? " is-takeout" : ""}`} role="group" aria-label="Text case">
                  {(doc.menuKey === "takeout"
                    ? [["none", "Regular"], ["titlecase", "Title Case"], ["uppercase", "UPPERCASE"], ["lowercase", "lowercase"]]
                    : [["none", "Regular case"], ["uppercase", "UPPERCASE"], ["lowercase", "lowercase"]]
                  ).map(([value, label]) => {
                    const active = nodes.every((n) => (getStyle(doc, n).textTransform || "none") === value);
                    return <Button key={value} active={active} aria-pressed={active}
                      disabled={nodes.every((n) => n.locked)}
                      onClick={() => stylePatch({ textTransform: value })}>{label}</Button>;
                  })}
                </div>
                <p className="ms-hint">{doc.menuKey === "takeout"
                  ? "Regular uses your saved spelling. Title Case formats the display without rewriting CMS text."
                  : "Regular case uses your saved spelling. Case changes use the edit scope above and do not rewrite CMS text."}</p>
                <div className="ms-fields-two">
                  <NumberField
                    label="Size · pt"
                    value={typo("fontSize").value}
                    mixed={typo("fontSize").mixed}
                    source={source("fontSize", "Size · pt")}
                    min={4}
                    max={240}
                    step={0.5}
                    onChange={(fontSize) => stylePatch({ fontSize })}
                  />
                  <NumberField
                    label="Line height"
                    value={typo("lineHeight").value}
                    mixed={typo("lineHeight").mixed}
                    source={source("lineHeight", "Line height")}
                    min={0.7}
                    max={4}
                    step={0.05}
                    onChange={(lineHeight) => stylePatch({ lineHeight })}
                  />
                </div>
                <NumberField
                  label="Letter spacing · pt"
                  value={typo("letterSpacing").value}
                    mixed={typo("letterSpacing").mixed}
                    source={source("letterSpacing", "Letter spacing · pt")}
                  min={-3}
                  max={20}
                  step={0.1}
                  onChange={(letterSpacing) => stylePatch({ letterSpacing })}
                />
                {nodes.length > 1 && nodes.every((n) => n.type === node.type) && <p className="ms-hint">Width and height scale each selected element proportionally. Heading ornaments grow away from the text.</p>}
              <div className="ms-button-row">
                  <Button
                    active={style.fontWeight === 700}
                    title="Bold"
                    onClick={() =>
                      stylePatch({
                        fontWeight: style.fontWeight === 700 ? 400 : 700,
                      })
                    }
                  >
                    <b>B</b>
                  </Button>
                  <Button
                    active={style.fontStyle === "italic"}
                    title="Italic"
                    onClick={() =>
                      stylePatch({
                        fontStyle:
                          style.fontStyle === "italic" ? "normal" : "italic",
                      })
                    }
                  >
                    <i>I</i>
                  </Button>
                  <span className="ms-toolbar-divider" />
                  {["left", "center", "right"].map((align) => (
                    <Button
                      key={align}
                      icon={align}
                      title={`Text align ${align}`}
                      active={style.align === align}
                      onClick={() => stylePatch({ align })}
                    />
                  ))}
                </div>
                <ColorField
                  label="Ink color"
                  value={style.fill}
                  onChange={(fill) => stylePatch({ fill })}
                />
              </PanelSection>
          )}
          {!styleGroup && (
            <StudioTransformPanel doc={doc} page={page} nodes={nodes} selected={selected} commitNodes={commitNodes} distribute={distribute} keyId={selected[0]} />
          )}
          {node.type === "text" && (
            <>

              {!styleGroup && nodes.length === 1 && (
                <PanelSection
                  title="Print copy"
                  aside={node.binding && <Icon name="link" size={15} />}
                >
                  <Field
                    label={
                      node.textOverride !== undefined
                        ? "Custom print text"
                        : node.binding
                        ? "Linked CMS text"
                        : "Text"
                    }
                  >
                    <button className="ms-text-edit-trigger" onClick={() => onEdit(node)}>
                      {resolveText(node, content) || "Edit text"}
                    </button>
                  </Field>
                  {node.binding && (
                    <>
                      <p className="ms-hint">
                        Edit directly on the page. Changes to linked text also update the CMS.
                      </p>
                      {node.textOverride !== undefined && (
                        <Button
                          icon="link"
                          onClick={() =>
                            patch((n) => {
                              const updated = { ...n };
                              delete updated.textOverride;
                              return updated;
                            }, true)
                          }
                        >
                          Restore CMS text
                        </Button>
                      )}
                    </>
                  )}
                </PanelSection>
              )}
            </>
          )}
          {node.type !== "text" && node.type !== "image" && (
            <PanelSection title="Appearance">
              <ColorField
                label="Fill / rule color"
                value={style.fill}
                onChange={(fill) => stylePatch({ fill })}
              />
              <NumberField
                label="Stroke width"
                value={style.strokeWidth || 0}
                min={0}
                max={30}
                step={0.25}
                onChange={(strokeWidth) => stylePatch({ strokeWidth })}
              />
              {node.type !== "line" && (
                <ColorField
                  label="Stroke color"
                  value={style.stroke || style.fill}
                  onChange={(stroke) => stylePatch({ stroke })}
                />
              )}
              {node.type === "rect" && (
                <NumberField
                  label="Corner radius"
                  value={style.radius || 0}
                  min={0}
                  max={200}
                  onChange={(radius) => stylePatch({ radius })}
                />
              )}
            </PanelSection>
          )}
          {node.type === "image" && (
            <PanelSection title="Image">
              <Field label="Image fit">
                <select
                  value={node.fit || "contain"}
                  onChange={(e) => patch({ fit: e.target.value })}
                >
                  <option value="contain">Fit entire image</option>
                  <option value="cover">Fill frame (crop)</option>
                </select>
              </Field>
            </PanelSection>
          )}
          {!styleGroup && (
            <PanelSection title="Organization">
              <Field label="Layer name">
                <input
                  value={node.name}
                  onChange={(e) => patch({ name: e.target.value })}
                />
              </Field>
              {node.type !== "text" && <Field label="Linked style role">
                <select
                  value={node.role}
                  onChange={(e) => patch({ role: e.target.value, style: {} })}
                >
                  {Object.entries(ROLES).map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
                </select>
              </Field>}
              <div className="ms-fields-two">
                <Button icon="up" onClick={() => reorder(1)}>
                  Forward
                </Button>
                <Button icon="down" onClick={() => reorder(-1)}>
                  Backward
                </Button>
                <Button onClick={group} disabled={nodes.length < 2}>
                  Group
                </Button>
                <Button
                  onClick={ungroup}
                  disabled={!nodes.some((n) => n.groupId)}
                >
                  Ungroup
                </Button>
              </div>
              <Field label="Move selected to page">
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) moveToPage(e.target.value);
                  }}
                >
                  <option value="">Choose page…</option>
                  {doc.pages
                    .filter((p) => p.id !== page.id)
                    .map((p) => (
                      <option key={p.id} value={p.id}>
                        {doc.pages.indexOf(p) + 1} · {p.name}
                      </option>
                    ))}
                </select>
              </Field>
              <div className="ms-button-row">
                <Button
                  icon={node.locked ? "unlock" : "lock"}
                  title={node.locked ? "Unlock selection" : "Lock selection"}
                  onClick={() => patch({ locked: !node.locked }, true)}
                />
                <Button
                  icon="duplicate"
                  title="Duplicate selection"
                  onClick={duplicate}
                />
                <Button
                  icon="trash"
                  title="Delete selection"
                  onClick={remove}
                />
              </div>
            </PanelSection>
          )}
        </>
      )}
    </aside>
  );
}
